import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { useFinancialYear } from '../context/FinancialYearContext';
import { toast } from 'sonner';
import axios from 'axios';
import {
  RefreshCw, Search, EyeOff, Eye, FileText, TrendingUp, CheckCircle2, Clock, XCircle,
  Link2, Pencil, X, Calendar, Users, IndianRupee, ArrowUpRight, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (v) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(v || 0);

const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-700',
  sent: 'bg-blue-100 text-blue-700',
  accepted: 'bg-emerald-100 text-emerald-700',
  approved: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-amber-100 text-amber-700',
  invoiced: 'bg-purple-100 text-purple-700'
};

const QUARTERS = [
  { value: 'all', label: 'Full FY' },
  { value: 'Q1', label: 'Q1 (Apr–Jun)' },
  { value: 'Q2', label: 'Q2 (Jul–Sep)' },
  { value: 'Q3', label: 'Q3 (Oct–Dec)' },
  { value: 'Q4', label: 'Q4 (Jan–Mar)' }
];

const EstimatesPage = () => {
  const { getAuthHeaders } = useAuth();
  const { selectedFY, fyOptions } = useFinancialYear();

  // List tab state
  const [estimates, setEstimates] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSalesperson, setFilterSalesperson] = useState('');
  const [showExcluded, setShowExcluded] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);

  // Dialog state - drilldown
  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Dialog state - exchange rate
  const [fxOpen, setFxOpen] = useState(false);
  const [fxRow, setFxRow] = useState(null);
  const [fxValue, setFxValue] = useState('');
  const [fxSaving, setFxSaving] = useState(false);

  // Dialog state - move to FY
  const [fyOpen, setFyOpen] = useState(false);
  const [fyRow, setFyRow] = useState(null);
  const [fyTarget, setFyTarget] = useState('');
  const [fySaving, setFySaving] = useState(false);

  // Customer summary tab state
  const [tab, setTab] = useState('list');
  const [csQuarter, setCsQuarter] = useState('all');
  const [csDateFrom, setCsDateFrom] = useState('');
  const [csDateTo, setCsDateTo] = useState('');
  const [csStatus, setCsStatus] = useState('');
  const [csData, setCsData] = useState(null);
  const [csLoading, setCsLoading] = useState(false);

  // Customer drilldown
  const [custDrillOpen, setCustDrillOpen] = useState(false);
  const [custDrill, setCustDrill] = useState(null);
  const [custDrillLoading, setCustDrillLoading] = useState(false);

  // Zoho deep-link base
  const [zohoBase, setZohoBase] = useState('');
  const [syncProgress, setSyncProgress] = useState(''); // e.g. "Syncing..." or ""

  useEffect(() => {
    axios.get(`${API}/zoho/settings`, { headers: getAuthHeaders() })
      .then(r => setZohoBase(r.data?.books_base_url || ''))
      .catch(() => {});
  }, [getAuthHeaders]);

  const zohoEstUrl = (id) => (zohoBase && id) ? `${zohoBase}#/quotes/${id}` : '';

  const fetchEstimates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 50 });
      if (selectedFY) params.append('financial_year', selectedFY);
      if (filterStatus) params.append('status', filterStatus);
      if (filterSalesperson) params.append('salesperson', filterSalesperson);
      if (search) params.append('search', search);
      if (showExcluded) params.append('show_excluded', 'true');

      const res = await axios.get(`${API}/zoho/estimates?${params}`, { headers: getAuthHeaders() });
      setEstimates(res.data.estimates);
      setTotal(res.data.total);
      setTotalPages(res.data.total_pages);
      setSummary(res.data.summary);
    } catch (err) {
      toast.error('Failed to load estimates');
    } finally {
      setLoading(false);
    }
  }, [page, selectedFY, filterStatus, filterSalesperson, search, showExcluded, getAuthHeaders]);

  const fetchCustomerSummary = useCallback(async () => {
    setCsLoading(true);
    try {
      const params = new URLSearchParams();
      if (csDateFrom && csDateTo) {
        params.append('date_from', csDateFrom);
        params.append('date_to', csDateTo);
      } else if (selectedFY) {
        params.append('financial_year', selectedFY);
        if (csQuarter && csQuarter !== 'all') params.append('quarter', csQuarter);
      }
      if (csStatus) params.append('status', csStatus);
      const res = await axios.get(`${API}/zoho/estimates/customer-summary?${params}`, { headers: getAuthHeaders() });
      setCsData(res.data);
    } catch (err) {
      toast.error('Failed to load customer summary');
    } finally {
      setCsLoading(false);
    }
  }, [selectedFY, csQuarter, csDateFrom, csDateTo, csStatus, getAuthHeaders]);

  useEffect(() => { if (tab === 'list') fetchEstimates(); }, [fetchEstimates, tab]);
  useEffect(() => { if (tab === 'customer') fetchCustomerSummary(); }, [fetchCustomerSummary, tab]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncProgress('Starting...');
    try {
      const res = await axios.post(`${API}/zoho/sync-estimates`, {}, { headers: getAuthHeaders() });
      const jobId = res.data?.job_id;
      if (!jobId) throw new Error('No job_id returned');
      toast.info('Sync started in background. Polling for progress...');
      // Poll every 3s
      const start = Date.now();
      const MAX_MS = 5 * 60 * 1000; // 5 minutes
      while (Date.now() - start < MAX_MS) {
        await new Promise(r => setTimeout(r, 3000));
        setSyncProgress(`Syncing... (${Math.round((Date.now() - start) / 1000)}s)`);
        const poll = await axios.get(`${API}/zoho/sync-jobs/${jobId}`, { headers: getAuthHeaders() });
        if (poll.data?.status === 'success') {
          const r = poll.data.result || {};
          toast.success(`Synced ${r.synced || 0} estimates (${r.skipped_non_deb || 0} non-Deb skipped)`);
          if (tab === 'list') fetchEstimates(); else fetchCustomerSummary();
          return;
        }
        if (poll.data?.status === 'failed') {
          toast.error(poll.data?.error || 'Sync failed');
          return;
        }
      }
      toast.error('Sync timed out after 5 minutes — check Recent Sync Jobs');
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Sync failed');
    } finally {
      setSyncing(false);
      setSyncProgress('');
    }
  };

  const toggleExclude = async (estimateId, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await axios.put(`${API}/zoho/estimates/${estimateId}/exclude`, {}, { headers: getAuthHeaders() });
      toast.success(res.data.excluded ? 'Excluded from calculations' : 'Included in calculations');
      fetchEstimates();
    } catch (err) {
      toast.error('Failed to toggle');
    }
  };

  const openDetail = async (estimateId) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const res = await axios.get(`${API}/zoho/estimates/detail/${estimateId}`, { headers: getAuthHeaders() });
      setDetail(res.data);
    } catch (err) {
      toast.error('Failed to load detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const openFxEditor = (row, e) => {
    if (e) e.stopPropagation();
    setFxRow(row);
    setFxValue(String(row.exchange_rate || ''));
    setFxOpen(true);
  };

  const saveFx = async () => {
    if (!fxRow) return;
    const rate = parseFloat(fxValue);
    if (!rate || rate <= 0) { toast.error('Enter a valid exchange rate'); return; }
    setFxSaving(true);
    try {
      await axios.put(`${API}/zoho/estimates/${fxRow.estimate_id}/exchange-rate`, { exchange_rate: rate }, { headers: getAuthHeaders() });
      toast.success(`Exchange rate updated to ${rate}`);
      setFxOpen(false);
      setFxRow(null);
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update rate');
    } finally {
      setFxSaving(false);
    }
  };

  const resetFx = async () => {
    if (!fxRow) return;
    setFxSaving(true);
    try {
      await axios.put(`${API}/zoho/estimates/${fxRow.estimate_id}/exchange-rate`,
        { exchange_rate: fxRow.exchange_rate, reset: true },
        { headers: getAuthHeaders() });
      toast.success('Manual override cleared. Next sync will pull live rate.');
      setFxOpen(false);
      fetchEstimates();
    } catch (err) {
      toast.error('Failed to reset');
    } finally {
      setFxSaving(false);
    }
  };

  const openFyMove = (row, e) => {
    if (e) e.stopPropagation();
    setFyRow(row);
    setFyTarget(row.manual_fy || selectedFY || '');
    setFyOpen(true);
  };

  const handleFyMove = async (clear) => {
    if (!fyRow) return;
    setFySaving(true);
    try {
      await axios.put(`${API}/zoho/estimates/${fyRow.estimate_id}/financial-year`,
        { manual_fy: clear ? null : fyTarget },
        { headers: getAuthHeaders() });
      toast.success(clear ? 'FY override cleared' : `Estimate moved to FY ${fyTarget}`);
      setFyOpen(false); setFyRow(null);
      fetchEstimates();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update FY');
    } finally {
      setFySaving(false);
    }
  };

  const openCustomerDrill = async (cust) => {
    setCustDrillOpen(true);
    setCustDrillLoading(true);
    setCustDrill({ customer_name: cust.customer_name });
    try {
      const params = new URLSearchParams({ customer_id: cust.customer_id });
      if (csDateFrom && csDateTo) {
        params.append('date_from', csDateFrom);
        params.append('date_to', csDateTo);
      } else if (selectedFY) {
        params.append('financial_year', selectedFY);
        if (csQuarter && csQuarter !== 'all') params.append('quarter', csQuarter);
      }
      if (csStatus) params.append('status', csStatus);
      const res = await axios.get(`${API}/zoho/estimates/customer-detail?${params}`, { headers: getAuthHeaders() });
      setCustDrill(res.data);
    } catch (err) {
      toast.error('Failed to load customer drilldown');
    } finally {
      setCustDrillLoading(false);
    }
  };

  const exportCustomerSummaryCSV = () => {    if (!csData?.customers?.length) return;
    const rows = [
      ['Customer', 'Count', 'Total (excl GST) INR', 'GST INR', 'Total (incl GST) INR'],
      ...csData.customers.map(c => [
        c.customer_name,
        c.count,
        c.total_excl_gst.toFixed(2),
        c.tax_total.toFixed(2),
        c.total_incl_gst.toFixed(2)
      ]),
      ['GRAND TOTAL', csData.grand_count, csData.grand_total_excl_gst.toFixed(2), csData.grand_tax_total.toFixed(2), csData.grand_total_incl_gst.toFixed(2)]
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const tag = csDateFrom && csDateTo ? `${csDateFrom}_to_${csDateTo}` : `${selectedFY}_${csQuarter}`;
    a.download = `estimates-customer-summary_${tag}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const statuses = ['draft', 'sent', 'accepted', 'approved', 'declined', 'rejected', 'expired', 'invoiced'];

  return (
    <div className="space-y-6" data-testid="estimates-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-indigo-950">Estimates</h1>
          <p className="text-sm text-slate-500">Zoho Books estimates pipeline · Salesperson: Deb · {selectedFY ? `FY ${selectedFY}` : 'all years'}</p>
        </div>
        <Button onClick={handleSync} disabled={syncing} className="bg-indigo-950 hover:bg-indigo-900" data-testid="sync-estimates-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? (syncProgress || 'Syncing...') : 'Sync from Zoho'}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="estimates-tabs">
          <TabsTrigger value="list" data-testid="tab-list"><FileText className="w-4 h-4 mr-1" /> List</TabsTrigger>
          <TabsTrigger value="customer" data-testid="tab-customer"><Users className="w-4 h-4 mr-1" /> Customer Summary</TabsTrigger>
        </TabsList>

        {/* ====== LIST TAB ====== */}
        <TabsContent value="list" className="space-y-6 mt-6">
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="est-kpi-pipeline">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-indigo-600" /></div>
                    <div>
                      <p className="text-xs text-indigo-600">Total Pipeline</p>
                      <p className="text-xl font-bold text-indigo-950">{fmt(summary.total_pipeline)}</p>
                      <p className="text-xs text-slate-500">{summary.total_count} estimates</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              {[
                { key: 'sent', label: 'Sent', color: 'blue', Icon: Clock },
                { key: 'invoiced', label: 'Invoiced', color: 'purple', Icon: CheckCircle2 },
                { key: 'declined', label: 'Declined', color: 'red', Icon: XCircle }
              ].map(({ key, label, color, Icon }) => {
                const d = summary.by_status[key] || { count: 0, total: 0 };
                const colorMap = {
                  blue: 'border-blue-200 bg-gradient-to-br from-blue-50 to-white',
                  purple: 'border-purple-200 bg-gradient-to-br from-purple-50 to-white',
                  red: 'border-red-200 bg-gradient-to-br from-red-50 to-white'
                };
                const iconBg = { blue: 'bg-blue-100 text-blue-600', purple: 'bg-purple-100 text-purple-600', red: 'bg-red-100 text-red-600' };
                const txt = { blue: 'text-blue-600', purple: 'text-purple-600', red: 'text-red-600' };
                return (
                  <Card key={key} className={`${colorMap[color]} cursor-pointer hover:shadow-md transition-shadow`} onClick={() => { setFilterStatus(key); setPage(1); }} data-testid={`est-kpi-${key}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 ${iconBg[color]} rounded-lg flex items-center justify-center`}><Icon className="w-5 h-5" /></div>
                        <div>
                          <p className={`text-xs ${txt[color]}`}>{label}</p>
                          <p className="text-xl font-bold text-slate-900">{fmt(d.total)}</p>
                          <p className="text-xs text-slate-500">{d.count} estimates</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {/* Filters */}
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input className="pl-9" placeholder="Search customer, estimate #, project..." value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchEstimates()} data-testid="est-search" />
            </div>
            <Select value={filterStatus || '__all'} onValueChange={(v) => { setFilterStatus(v === '__all' ? '' : v); setPage(1); }}>
              <SelectTrigger className="w-36" data-testid="est-status-filter"><SelectValue placeholder="All Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All Status</SelectItem>
                {statuses.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input className="w-40" placeholder="Salesperson..." value={filterSalesperson} onChange={(e) => setFilterSalesperson(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchEstimates()} data-testid="est-salesperson-filter" />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={showExcluded} onChange={(e) => { setShowExcluded(e.target.checked); setPage(1); }} className="rounded border-slate-300" data-testid="est-show-excluded" />
              <span className="text-slate-600">Show Excluded</span>
            </label>
            {(filterStatus || filterSalesperson || search) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterStatus(''); setFilterSalesperson(''); setSearch(''); setPage(1); }} className="text-slate-500">Clear</Button>
            )}
          </div>

          {/* Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="estimates-table">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-4 font-medium text-slate-500">Estimate #</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-500">Date</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-500">Customer</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-500">Project ID</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-500">Branch</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-500">Amount</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-500">Status</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-500">Invoiced</th>
                      <th className="text-center py-3 px-4 font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading && <tr><td colSpan="9" className="py-8 text-center text-slate-400">Loading...</td></tr>}
                    {!loading && estimates.map(est => (
                      <tr
                        key={est.estimate_id}
                        className={`border-b hover:bg-indigo-50/40 cursor-pointer ${est.excluded ? 'opacity-50 bg-red-50/30' : ''}`}
                        onClick={() => openDetail(est.estimate_id)}
                        data-testid={`est-row-${est.estimate_id}`}
                      >
                        <td className="py-3 px-4 font-mono text-xs font-medium text-indigo-700">{est.estimate_number}</td>
                        <td className="py-3 px-4 text-slate-600">
                          {est.date}
                          {est.manual_fy && (
                            <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px] py-0 px-1" title={`Moved to FY ${est.manual_fy}`}>
                              FY {est.manual_fy}
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 font-medium">{est.customer_name}</td>
                        <td className="py-3 px-4 text-xs font-mono text-slate-500">{est.crm_project_id || '-'}</td>
                        <td className="py-3 px-4 text-xs">{est.branch_name || '-'}</td>
                        <td className="py-3 px-4 text-right font-mono font-medium">
                          {est.currency_code !== 'INR' ? (
                            <div>
                              <span>{est.currency_code} {est.total?.toLocaleString('en-IN')}</span>
                              <br />
                              <span className="text-xs text-slate-500">
                                {fmt(est.total_inr)}
                                <button
                                  className="ml-1 text-indigo-500 hover:text-indigo-700 inline-flex items-center"
                                  onClick={(e) => openFxEditor(est, e)}
                                  title={`Edit exchange rate (current: ${est.exchange_rate}${est.manual_exchange_rate ? ' — manual' : ''})`}
                                  data-testid={`fx-edit-${est.estimate_id}`}
                                >
                                  <Pencil className="w-3 h-3" />
                                </button>
                                {est.manual_exchange_rate && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px] py-0 px-1">manual</Badge>}
                              </span>
                            </div>
                          ) : fmt(est.total)}
                        </td>
                        <td className="py-3 px-4 text-center"><Badge className={STATUS_COLORS[est.status] || 'bg-slate-100 text-slate-700'}>{est.status}</Badge></td>
                        <td className="py-3 px-4 text-center">
                          {est.linked_invoice_ids?.length > 0
                            ? <Badge className="bg-purple-100 text-purple-700"><Link2 className="w-3 h-3 mr-1 inline" />{est.linked_invoice_ids.length}</Badge>
                            : <span className="text-slate-400">-</span>}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex items-center justify-center gap-1">
                            {zohoBase && (
                              <a
                                href={zohoEstUrl(est.estimate_id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                title="Open in Zoho Books"
                                className="inline-flex items-center justify-center w-8 h-8 rounded hover:bg-indigo-50 text-indigo-600"
                                data-testid={`est-zoho-link-${est.estimate_id}`}
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            )}
                            <Button variant="ghost" size="sm" onClick={(e) => openFyMove(est, e)} title={est.manual_fy ? `Currently moved to FY ${est.manual_fy}` : 'Move to a different FY'} data-testid={`est-fy-move-${est.estimate_id}`}>
                              <ArrowUpRight className={`w-4 h-4 ${est.manual_fy ? 'text-amber-600' : 'text-slate-400'}`} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={(e) => toggleExclude(est.estimate_id, e)} title={est.excluded ? 'Include' : 'Exclude'} data-testid={`est-toggle-${est.estimate_id}`}>
                              {est.excluded ? <Eye className="w-4 h-4 text-emerald-600" /> : <EyeOff className="w-4 h-4 text-red-500" />}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {!loading && estimates.length === 0 && <tr><td colSpan="9" className="py-8 text-center text-slate-400">No estimates found. Click "Sync from Zoho" to fetch.</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-slate-500">{total} estimates total</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
                <span className="text-sm py-2 px-3">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ====== CUSTOMER SUMMARY TAB ====== */}
        <TabsContent value="customer" className="space-y-6 mt-6" data-testid="customer-summary-pane">
          {/* Filters */}
          <Card className="border-slate-200">
            <CardContent className="p-4 flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Quarter (within {selectedFY || 'FY'})</Label>
                <Select value={csQuarter} onValueChange={(v) => { setCsQuarter(v); setCsDateFrom(''); setCsDateTo(''); }}>
                  <SelectTrigger className="w-44" data-testid="cs-quarter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {QUARTERS.map(q => <SelectItem key={q.value} value={q.value}>{q.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Custom From</Label>
                <Input type="date" value={csDateFrom} onChange={(e) => setCsDateFrom(e.target.value)} className="w-44" data-testid="cs-date-from" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Custom To</Label>
                <Input type="date" value={csDateTo} onChange={(e) => setCsDateTo(e.target.value)} className="w-44" data-testid="cs-date-to" />
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
                <Select value={csStatus || '__all'} onValueChange={(v) => setCsStatus(v === '__all' ? '' : v)}>
                  <SelectTrigger className="w-36" data-testid="cs-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All Status</SelectItem>
                    {statuses.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {(csDateFrom || csDateTo || csStatus || csQuarter !== 'all') && (
                <Button variant="ghost" size="sm" onClick={() => { setCsDateFrom(''); setCsDateTo(''); setCsStatus(''); setCsQuarter('all'); }} className="text-slate-500">
                  <X className="w-3 h-3 mr-1" />Clear
                </Button>
              )}
              <div className="ml-auto">
                <Button variant="outline" size="sm" onClick={exportCustomerSummaryCSV} disabled={!csData?.customers?.length} data-testid="cs-export">Export CSV</Button>
              </div>
            </CardContent>
          </Card>

          {/* Grand totals */}
          {csData && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-white" data-testid="cs-kpi-customers">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center"><Users className="w-5 h-5 text-indigo-600" /></div>
                    <div>
                      <p className="text-xs text-indigo-600">Customers</p>
                      <p className="text-xl font-bold text-indigo-950">{csData.customers.length}</p>
                      <p className="text-xs text-slate-500">{csData.grand_count} estimates</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-slate-200 bg-gradient-to-br from-slate-50 to-white" data-testid="cs-kpi-excl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center"><IndianRupee className="w-5 h-5 text-slate-600" /></div>
                    <div>
                      <p className="text-xs text-slate-600">Total (excl GST)</p>
                      <p className="text-xl font-bold text-slate-900">{fmt(csData.grand_total_excl_gst)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white" data-testid="cs-kpi-gst">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Calendar className="w-5 h-5 text-amber-600" /></div>
                    <div>
                      <p className="text-xs text-amber-600">GST Total</p>
                      <p className="text-xl font-bold text-amber-700">{fmt(csData.grand_tax_total)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="cs-kpi-incl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center"><TrendingUp className="w-5 h-5 text-emerald-600" /></div>
                    <div>
                      <p className="text-xs text-emerald-600">Total (incl GST)</p>
                      <p className="text-xl font-bold text-emerald-700">{fmt(csData.grand_total_incl_gst)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Period info */}
          {csData?.filters && (
            <p className="text-xs text-slate-500">
              Period: <strong>{csData.filters.date_from || '—'}</strong> to <strong>{csData.filters.date_to || '—'}</strong>
              {csData.filters.status ? ` · Status: ${csData.filters.status}` : ''}
              {csData.customers.length === 0 && (csData.grand_total_excl_gst === 0 && csData.grand_total_incl_gst > 0)
                ? ' · ⚠ Re-sync needed to capture sub_total/tax fields.' : ''}
            </p>
          )}

          {/* Customer table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="customer-summary-table">
                  <thead>
                    <tr className="border-b bg-slate-50">
                      <th className="text-left py-3 px-4 font-medium text-slate-500">#</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-500">Customer</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-500">Estimates</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-500">Excl. GST</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-500">GST</th>
                      <th className="text-right py-3 px-4 font-medium text-slate-500">Incl. GST</th>
                    </tr>
                  </thead>
                  <tbody>
                    {csLoading && <tr><td colSpan="6" className="py-8 text-center text-slate-400">Loading...</td></tr>}
                    {!csLoading && csData?.customers.map((c, i) => (
                      <tr
                        key={c.customer_id || c.customer_name}
                        className="border-b hover:bg-indigo-50/50 cursor-pointer"
                        onClick={() => openCustomerDrill(c)}
                        data-testid={`cs-row-${i}`}
                        title="Click for estimate-level drilldown"
                      >
                        <td className="py-3 px-4 text-slate-400 font-mono text-xs">{i + 1}</td>
                        <td className="py-3 px-4 font-medium text-indigo-700">{c.customer_name}</td>
                        <td className="py-3 px-4 text-right font-mono">{c.count}</td>
                        <td className="py-3 px-4 text-right font-mono text-slate-700">{fmt(c.total_excl_gst)}</td>
                        <td className="py-3 px-4 text-right font-mono text-amber-700">{fmt(c.tax_total)}</td>
                        <td className="py-3 px-4 text-right font-mono font-semibold text-emerald-700">{fmt(c.total_incl_gst)}</td>
                      </tr>
                    ))}
                    {!csLoading && csData?.customers.length === 0 && (
                      <tr><td colSpan="6" className="py-8 text-center text-slate-400">No estimates in this period.</td></tr>
                    )}
                  </tbody>
                  {!csLoading && csData?.customers.length > 0 && (
                    <tfoot>
                      <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold">
                        <td className="py-3 px-4"></td>
                        <td className="py-3 px-4">GRAND TOTAL</td>
                        <td className="py-3 px-4 text-right font-mono">{csData.grand_count}</td>
                        <td className="py-3 px-4 text-right font-mono">{fmt(csData.grand_total_excl_gst)}</td>
                        <td className="py-3 px-4 text-right font-mono text-amber-700">{fmt(csData.grand_tax_total)}</td>
                        <td className="py-3 px-4 text-right font-mono text-emerald-700">{fmt(csData.grand_total_incl_gst)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ====== DRILLDOWN DIALOG ====== */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="estimate-detail-dialog">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3">
              <div>
                <DialogTitle>{detail?.estimate_number || 'Estimate Details'}</DialogTitle>
                <DialogDescription>{detail?.customer_name}</DialogDescription>
              </div>
              {detail?.estimate_id && zohoBase && (
                <a
                  href={zohoEstUrl(detail.estimate_id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100"
                  data-testid="detail-zoho-link"
                >
                  <ExternalLink className="w-3 h-3" />Open in Zoho
                </a>
              )}
            </div>
          </DialogHeader>
          {detailLoading && <div className="py-10 text-center text-slate-500">Loading...</div>}
          {!detailLoading && detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Info label="Date" value={detail.date} />
                <Info label="Expiry" value={detail.expiry_date || '-'} />
                <Info label="Status" value={<Badge className={STATUS_COLORS[detail.status] || 'bg-slate-100'}>{detail.status}</Badge>} />
                <Info label="Salesperson" value={detail.salesperson_name || '-'} />
                <Info label="Project ID" value={detail.crm_project_id || '-'} />
                <Info label="Branch" value={detail.branch_name || '-'} />
                <Info label="Client Type" value={detail.client_type || '-'} />
                <Info label="Reference" value={detail.reference_number || '-'} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-indigo-50 rounded-lg">
                <Info label="Sub Total (excl GST)" value={`${detail.currency_code} ${(detail.sub_total || 0).toLocaleString('en-IN')}`} />
                <Info label="Tax / GST" value={`${detail.currency_code} ${(detail.tax_total || 0).toLocaleString('en-IN')}`} />
                <Info label="Total (incl GST)" value={`${detail.currency_code} ${(detail.total || 0).toLocaleString('en-IN')}`} />
                <Info label="Total in INR" value={fmt(detail.total_inr)} />
              </div>

              {detail.currency_code !== 'INR' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                  <div className="text-xs">
                    Exchange Rate: <strong className="font-mono">{detail.exchange_rate}</strong>
                    {detail.manual_exchange_rate && <Badge className="ml-2 bg-amber-100 text-amber-700">Manual Override</Badge>}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => { setDetailOpen(false); openFxEditor(detail); }}><Pencil className="w-3 h-3 mr-1" />Edit Rate</Button>
                </div>
              )}

              {detail.line_items?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-slate-700 mb-2 mt-4">Line Items</h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left py-2 px-3">Item</th>
                          <th className="text-left py-2 px-3">Description</th>
                          <th className="text-right py-2 px-3">Qty</th>
                          <th className="text-right py-2 px-3">Rate</th>
                          <th className="text-right py-2 px-3">Tax %</th>
                          <th className="text-right py-2 px-3">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.line_items.map((li, i) => (
                          <tr key={li.item_id || i} className="border-t">
                            <td className="py-2 px-3 font-medium">{li.name}</td>
                            <td className="py-2 px-3 text-slate-500 max-w-xs truncate" title={li.description}>{li.description}</td>
                            <td className="py-2 px-3 text-right font-mono">{li.quantity} {li.unit}</td>
                            <td className="py-2 px-3 text-right font-mono">{li.rate?.toLocaleString('en-IN')}</td>
                            <td className="py-2 px-3 text-right font-mono">{li.tax_percentage || 0}%</td>
                            <td className="py-2 px-3 text-right font-mono font-semibold">{li.item_total?.toLocaleString('en-IN')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {detail.linked_invoice_ids?.length > 0 && (
                <div className="text-xs"><strong>Linked Invoices:</strong> {detail.linked_invoice_ids.join(', ')}</div>
              )}
              {detail.notes && <Info label="Notes" value={detail.notes} />}
              {detail.terms && <Info label="Terms" value={detail.terms} />}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ====== EXCHANGE RATE DIALOG ====== */}
      <Dialog open={fxOpen} onOpenChange={setFxOpen}>
        <DialogContent className="max-w-md" data-testid="fx-dialog">
          <DialogHeader>
            <DialogTitle>Edit Exchange Rate</DialogTitle>
            <DialogDescription>
              {fxRow?.estimate_number} — {fxRow?.customer_name}<br />
              Currency: <strong>{fxRow?.currency_code}</strong> · Amount: <strong>{fxRow?.currency_code} {fxRow?.total?.toLocaleString('en-IN')}</strong>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label className="text-xs text-slate-500">Exchange Rate (1 {fxRow?.currency_code} = ? INR)</Label>
            <Input
              type="number"
              step="0.0001"
              value={fxValue}
              onChange={(e) => setFxValue(e.target.value)}
              placeholder="e.g., 83.45"
              data-testid="fx-input"
            />
            <p className="text-xs text-slate-500">
              New INR total: <strong>{fmt((parseFloat(fxValue) || 0) * (fxRow?.total || 0))}</strong>
            </p>
            {fxRow?.manual_exchange_rate && (
              <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                Already overridden manually. You can reset to allow next sync to refresh from Zoho.
              </p>
            )}
          </div>
          <DialogFooter>
            {fxRow?.manual_exchange_rate && (
              <Button variant="outline" onClick={resetFx} disabled={fxSaving} data-testid="fx-reset">Reset to Auto</Button>
            )}
            <Button variant="ghost" onClick={() => setFxOpen(false)}>Cancel</Button>
            <Button onClick={saveFx} disabled={fxSaving} className="bg-indigo-950 hover:bg-indigo-900" data-testid="fx-save">
              {fxSaving ? 'Saving...' : 'Save Rate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ====== MOVE TO FY DIALOG ====== */}
      <Dialog open={fyOpen} onOpenChange={setFyOpen}>
        <DialogContent className="max-w-md" data-testid="est-fy-dialog">
          <DialogHeader>
            <DialogTitle>Move Estimate to a different FY</DialogTitle>
            <DialogDescription>
              {fyRow && (<span>Estimate <strong>{fyRow.estimate_number}</strong> ({fyRow.customer_name}) · raised on {fyRow.date}</span>)}
              <br />
              Set an internal FY override. Dashboard, Pipeline KPI and Customer Summary will count this estimate towards the chosen FY (not its raise-date FY).
            </DialogDescription>
          </DialogHeader>
          <div className="py-2 space-y-3">
            <Label className="text-sm">Target Financial Year</Label>
            <Select value={fyTarget} onValueChange={setFyTarget}>
              <SelectTrigger className="mt-1" data-testid="est-fy-target"><SelectValue placeholder="Choose FY" /></SelectTrigger>
              <SelectContent>
                {(fyOptions || []).map(fy => (
                  <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fyRow?.manual_fy && (
              <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                Currently overridden to <strong>FY {fyRow.manual_fy}</strong>. Reset to use the raise-date FY.
              </p>
            )}
          </div>
          <DialogFooter>
            {fyRow?.manual_fy && (
              <Button variant="outline" onClick={() => handleFyMove(true)} disabled={fySaving} data-testid="est-fy-clear">Reset to Auto</Button>
            )}
            <Button variant="ghost" onClick={() => setFyOpen(false)}>Cancel</Button>
            <Button className="bg-indigo-950 hover:bg-indigo-900" onClick={() => handleFyMove(false)} disabled={fySaving || !fyTarget} data-testid="est-fy-confirm">
              {fySaving ? 'Saving...' : `Move to FY ${fyTarget}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* ====== CUSTOMER DRILLDOWN DIALOG ====== */}
      <Dialog open={custDrillOpen} onOpenChange={setCustDrillOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="customer-drill-dialog">
          <DialogHeader>
            <DialogTitle>{custDrill?.customer_name || 'Customer Estimates'}</DialogTitle>
            <DialogDescription>
              {custDrill?.filters && (
                <span>
                  Period: <strong>{custDrill.filters.date_from || '—'}</strong> to <strong>{custDrill.filters.date_to || '—'}</strong>
                  {custDrill.filters.quarter ? ` · Quarter ${custDrill.filters.quarter}` : ''}
                  {custDrill.filters.status ? ` · Status ${custDrill.filters.status}` : ''}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {custDrillLoading && <div className="py-10 text-center text-slate-500">Loading...</div>}
          {!custDrillLoading && custDrill?.estimates && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-indigo-50 rounded-lg text-sm">
                <div>
                  <p className="text-xs text-slate-500">Estimates</p>
                  <p className="text-lg font-bold text-indigo-950">{custDrill.count}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total (excl GST)</p>
                  <p className="text-lg font-bold text-slate-900">{fmt(custDrill.total_excl_gst)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">GST</p>
                  <p className="text-lg font-bold text-amber-700">{fmt(custDrill.tax_total)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Total (incl GST)</p>
                  <p className="text-lg font-bold text-emerald-700">{fmt(custDrill.total_incl_gst)}</p>
                </div>
              </div>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm" data-testid="customer-drill-table">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left py-2 px-3 font-medium text-slate-500">Estimate #</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-500">Date</th>
                      <th className="text-left py-2 px-3 font-medium text-slate-500">Project ID</th>
                      <th className="text-center py-2 px-3 font-medium text-slate-500">Status</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-500">Excl. GST</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-500">GST</th>
                      <th className="text-right py-2 px-3 font-medium text-slate-500">Incl. GST</th>
                      <th className="text-center py-2 px-3 font-medium text-slate-500">Zoho</th>
                    </tr>
                  </thead>
                  <tbody>
                    {custDrill.estimates.map(e => (
                      <tr
                        key={e.estimate_id}
                        className="border-t hover:bg-indigo-50/40 cursor-pointer"
                        onClick={() => { setCustDrillOpen(false); openDetail(e.estimate_id); }}
                        data-testid={`drill-row-${e.estimate_id}`}
                      >
                        <td className="py-2 px-3 font-mono text-xs font-medium text-indigo-700">{e.estimate_number}</td>
                        <td className="py-2 px-3 text-slate-600">
                          {e.date}
                          {e.manual_fy && <Badge className="ml-1 bg-amber-100 text-amber-700 text-[10px] py-0 px-1">FY {e.manual_fy}</Badge>}
                        </td>
                        <td className="py-2 px-3 text-xs font-mono text-slate-500">{e.crm_project_id || '-'}</td>
                        <td className="py-2 px-3 text-center"><Badge className={STATUS_COLORS[e.status] || 'bg-slate-100'}>{e.status}</Badge></td>
                        <td className="py-2 px-3 text-right font-mono">{fmt(e.sub_total_inr)}</td>
                        <td className="py-2 px-3 text-right font-mono text-amber-700">{fmt(e.tax_total_inr)}</td>
                        <td className="py-2 px-3 text-right font-mono font-semibold text-emerald-700">{fmt(e.total_inr)}</td>
                        <td className="py-2 px-3 text-center">
                          {zohoBase && (
                            <a
                              href={zohoEstUrl(e.estimate_id)}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(ev) => ev.stopPropagation()}
                              className="inline-flex items-center justify-center w-7 h-7 rounded text-indigo-600 hover:bg-indigo-50"
                              title="Open in Zoho Books"
                              data-testid={`drill-zoho-link-${e.estimate_id}`}
                            >
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                        </td>
                      </tr>
                    ))}
                    {custDrill.estimates.length === 0 && (
                      <tr><td colSpan="8" className="py-6 text-center text-slate-400">No estimates in this period.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500">Click a row to open the full estimate detail.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-500 mb-1">{label}</p>
    <div className="text-sm font-medium text-slate-800">{value}</div>
  </div>
);

export default EstimatesPage;
